import type {
  ScenarioStoryDefinition,
  StoryBackdropDefinition,
  StoryEntry,
  StoryLine,
  StorySpeakerDefinition,
  StorySpeakerId,
  StoryTitle,
} from "./types.js";

function line(
  speaker: StorySpeakerId,
  text: string,
  options: Omit<StoryLine, "kind" | "speaker" | "text"> = {},
): StoryLine {
  return { kind: "line", speaker, text, ...options };
}

function narration(
  text: string,
  options: Omit<StoryLine, "kind" | "speaker" | "text" | "tone"> = {},
): StoryLine {
  return line("narrator", text, { tone: "narration", ...options });
}

function title(
  eyebrow: string,
  text: string,
  options: Omit<StoryTitle, "kind" | "eyebrow" | "text"> = {},
): StoryTitle {
  return { kind: "title", eyebrow, text, ...options };
}

const speakers: Record<StorySpeakerId, StorySpeakerDefinition> = {
  narrator: {
    id: "narrator",
    name: "",
    role: "",
    symbol: "✦",
    portraitId: "",
  },
  protagonist: {
    id: "protagonist",
    name: "新人",
    role: "今夜が初舞台のしもべ",
    symbol: "砂",
    portraitId: "protagonist",
  },
  manager: {
    id: "manager",
    name: "支配人",
    role: "《羅針盤》",
    symbol: "針",
    portraitId: "manager",
  },
  flint: {
    id: "flint",
    name: "《火打石》",
    role: "最初の火花を落とす者",
    symbol: "火",
    portraitId: "flint",
  },
  man: {
    id: "man",
    name: "男",
    role: "犬の飼い主",
    symbol: "灯",
    portraitId: "man",
  },
  dog: {
    id: "dog",
    name: "犬",
    role: "散歩を待っていたもの",
    symbol: "足",
    portraitId: "dog",
  },
};

const backdrops: Record<string, StoryBackdropDefinition> = {
  dark: {
    id: "dark",
    imageId: "",
    label: "暗闇",
    mode: "dark",
  },
  backstage: {
    id: "backstage",
    imageId: "backstage",
    label: "古い緞帳に囲まれた夢の劇場の舞台裏",
    mode: "scene",
  },
  theater: {
    id: "theater",
    imageId: "theater",
    label: "黒い水面の《窓》がある円形劇場",
    mode: "theater",
  },
  nightEyes: {
    id: "nightEyes",
    imageId: "nightEyes",
    label: "暗い客席から舞台を見る《夜の目》",
    mode: "night-eyes",
  },
  windowActive: {
    id: "windowActive",
    imageId: "theater",
    label: "夕暮れの散歩道が水面へ開く《窓》",
    mode: "window-active",
  },
  dreamStreet: {
    id: "dreamStreet",
    imageId: "dreamStreet",
    label: "同じ街灯と分かれ道が繰り返す夕暮れの散歩道",
    mode: "scene",
  },
  loopingWalkStill: {
    id: "loopingWalkStill",
    imageId: "loopingWalkStill",
    label: "帰らずの散歩道を歩く男と犬",
    mode: "scene",
  },
  ballReturnStill: {
    id: "ballReturnStill",
    imageId: "ballReturnStill",
    label: "赤いボールを持って男のもとへ帰る犬",
    mode: "scene",
  },
  walkingHomeStill: {
    id: "walkingHomeStill",
    imageId: "walkingHomeStill",
    label: "家の明かりへ並んで帰る男と犬",
    mode: "scene",
  },
  morningBedroom: {
    id: "morningBedroom",
    imageId: "morningBedroom",
    label: "朝の光が差す男の寝室",
    mode: "scene",
  },
  morningStill: {
    id: "morningStill",
    imageId: "morningStill",
    label: "朝、男の手へ鼻先を寄せる老犬",
    mode: "scene",
  },
};

const opening: readonly StoryEntry[] = [
  narration(
    "暗い舞台裏。主人公が目を開けると、古い緞帳の前に立っていた。手には見覚えのない公演札。胸元には、自分のものだと分かる制服。",
    { backdrop: "backstage" },
  ),
  narration("ここへどうやって来たのかは思い出せない。けれど、これから仕事が始まることだけは知っている。"),
  line("protagonist", "よし。今日から、がんばろう"),
  narration("一歩踏み出したところで、靴先が緞帳の裾へ引っかかった。"),
  line("protagonist", "わっ"),
  narration("公演札が手から離れ、舞台の方へ滑っていく。白い手袋が、それを拾い上げた。"),
  line("manager", "初日から、ずいぶん勢いのあるご入場でございますね"),
  line("protagonist", "すみません。緊張しているだけです。いつも転ぶわけではありません"),
  line("manager", "承知いたしました。今夜が特別ということにしておきましょう"),
  line("manager", "ようこそ、当劇場へ"),
  narration(
    "緞帳の隙間が開く。舞台の中央には、底の見えない黒い水をたたえた円形の《窓》。古い木の床と、何列もの客席がその周りを囲んでいる。",
    { backdrop: "theater" },
  ),
  narration("客席に人影はない。代わりに、暗がりのあちこちで小さな目が瞬いている。", {
    backdrop: "nightEyes",
  }),
  line("protagonist", "あちらの方々は"),
  line("manager", "お客様でございます"),
  line("protagonist", "目しか見えませんけど"),
  line("manager", "見るためにいらした方々ですので、それで不足はないのでしょう"),
  narration("客席の目が一斉に主人公へ向く。主人公は一歩だけ支配人の後ろへ隠れ、それから頭を下げた。"),
  line("protagonist", "本日からお世話になります"),
  narration("暗がりから、紙を一枚だけめくるような音が返る。"),
  line("manager", "わたくし共は、《夜の目》の皆様とお呼びしております"),
  line("protagonist", "ずっと、ああして見ているんですか"),
  line("manager", "興味を失うまでは"),
  line("protagonist", "では、失わせないようにがんばります"),
  line("manager", "頼もしいお言葉です"),
  narration("支配人は主人公を《窓》のそばへ案内する。黒い水面の下で、大きな光と小さな光が揺れていた。", {
    backdrop: "theater",
  }),
  line("manager", "今宵のご主人は、一人と一匹"),
  line("protagonist", "二人の夢を、同時に？"),
  line("manager", "劇場が二つの夢を結び、一つの演目へ仕立てます"),
  line("manager", "あなた方しもべには、それぞれ一方のご主人をご担当いただきます"),
  line("protagonist", "夢を好きなように作ればいいんですね"),
  line("manager", "いいえ。夢はすでに、ご主人の中にございます"),
  line("manager", "しもべができるのは、物を置き、目を向け、ほんの少し流れを変えること"),
  line("manager", "その少しで、どのような朝へたどり着くか。それをお楽しみいただくのが、当劇場でございます"),
  line("protagonist", "夢の中にも、新人研修ってあるんですね"),
  line("manager", "夢の中にしかない研修もございます"),
  narration("舞台袖から短いあくびが聞こえる。壁にもたれた若い女性が、火のない黒いパイプを手にしていた。", {
    backdrop: "backstage" },
  ),
  line("flint", "研修になるほどの事件ならいいけど"),
  line("protagonist", "事件？"),
  line("flint", "今夜の夢。年寄り二人が歩くだけ"),
  line("protagonist", "一人と一匹です"),
  line("flint", "細かいね、新人"),
  line("flint", "事件になるかも分からない、退屈な散歩"),
  line("protagonist", "平和なら、それが一番では？"),
  line("flint", "平和なまま終わるなら、客席は家で寝てる"),
  line("protagonist", "ここへ来る方も、寝ているのでは？"),
  narration("《火打石》は少しだけ目を開ける。パイプの先で、ごく小さな火花が弾けた。"),
  line("flint", "君、案外おもしろいね"),
  line("manager", "お話の続きは、公演の中でお願いいたします", { backdrop: "theater" }),
  narration("杖が舞台を一度叩き、劇場の明かりが落ちる。一本の光が、支配人だけを照らした。", {
    backdrop: "nightEyes",
  }),
  line("manager", "皆様方、長らくお待たせいたしました"),
  line("manager", "本日の演目、題しまして、夢幻小景《帰らずの散歩道》"),
  line("manager", "どこまでも続く道。何度通っても変わらぬ景色"),
  line("manager", "帰れぬ者は、帰り道を忘れたのか。それとも、帰ることを望まぬのか"),
  line("manager", "今宵のしもべは、当劇場期待の新人。そして、最初の火花を見逃さぬ《火打石》でございます"),
  line("manager", "どうぞ夜明けまで、ごゆるりとお楽しみください"),
  narration("乾いた拍手が鳴る。《火打石》が《窓》へ片手を浸した。", { cue: "applause" }),
  line("flint", "小さい方が私。大きい方が君"),
  line("protagonist", "犬と、男の人"),
  line("flint", "目を閉じないこと。夢の方が君を見ていてもね"),
  narration(
    "主人公も黒い水へ手を入れる。古い革の首輪、湿った土、使われなくなった食器、棚の下へ転がったゴムの匂い。それから、家へ帰れない不安。",
    { backdrop: "windowActive", cue: "water" },
  ),
  title("第一章", "帰る匂い", { backdrop: "windowActive" }),
];

const beforeTurn: ScenarioStoryDefinition["beforeTurn"] = {
  1: [
    narration(
      "夕暮れの住宅街を、男と犬が歩いている。現実では老いた犬も、夢の中では軽い足取りで先へ駆けていく。",
      { backdrop: "loopingWalkStill" },
    ),
    line("man", "おい。そんなに急ぐなよ", { backdrop: "dreamStreet" }),
    line("dog", "わん！", { cue: "dog" }),
    line("protagonist", "わんちゃん、すごくうれしそうです"),
    line("flint", "まずは《雲のゆりかご》を中央へ置く。男にも夢が届くよう、同じレーンへ注目して"),
  ],
  2: [
    narration("犬が街灯の根元を嗅ぎ、また走り出す。男の前には、先ほども見た赤い郵便受けと黄色い傘が現れた。", {
      backdrop: "dreamStreet",
    }),
    line("man", "……さっきも、ここを通らなかったか"),
    narration("《火打石》がパイプの灰を指先で叩く。橙色の火花が一つ《窓》へ落ち、街灯がもう一本増える。"),
    line("protagonist", "今、増やしましたね"),
    line("flint", "気づけるようにしただけ"),
    line("protagonist", "帰れないことに？"),
    line("flint", "何も起きない夢は、自分が何を恐れてるかにも気づかない"),
    line("flint", "ここからは自分で選べる。カードを使わず、注目だけ変えることもできる"),
    line("flint", "ただし視線は、一時間に隣のレーンまで。端から反対端へ一息には動かせない"),
  ],
  3: [
    narration("二度目の火花。同じ郵便受けが道の両側へ並び、犬は新しい匂いを喜んでさらに先へ駆ける。", {
      backdrop: "dreamStreet",
    }),
    line("protagonist", "犬は楽しくても、男の人は怖がっています"),
    line("flint", "同じ火を見て、暖かいと思うか、火事だと思うか。そこまでは私の仕事じゃない"),
    line("protagonist", "始めるだけなんですか"),
    line("flint", "始まらなければ、何も選べない"),
  ],
  4: [
    narration("三度目の火花。夕暮れが濃くなり、家々の明かりが消える。男と犬の足音だけが残った。", {
      backdrop: "dreamStreet",
    }),
    line("flint", "二人が同じレーンを見れば、そこにある夢は一段強く響く。共鳴だ"),
    line("protagonist", "よい夢なら、同じ場所を見た方が得ですね"),
    line("flint", "悪い夢も、よく響く"),
  ],
  5: [
    narration("男は犬の首輪へ手を伸ばす。犬が身をすくめると、無理に連れ戻す代わりにその場へしゃがんだ。", {
      backdrop: "loopingWalkStill",
    }),
    line("man", "お前は、平気なのか"),
    narration("犬は少し先へ行っては必ず男のそばへ戻る。また先へ行き、戻る。"),
    line("protagonist", "この子は、道を歩きたいんじゃない"),
    line("flint", "散歩の夢なのに？"),
    line("protagonist", "この人と、散歩したいんです"),
    line("flint", "似たようなものじゃない"),
    line("protagonist", "同じなら、戻ってくる必要はありません"),
    line("flint", "なら、献身《読み替え》を《伸び続ける散歩道》へ向けて。二人の記憶に手を入れてみな"),
  ],
  6: [
    narration("主人公の手には、かつて赤かったらしい傷だらけのゴムのボールが載っている。", {
      backdrop: "dreamStreet",
    }),
    line("flint", "遊ばせるの？　道を長くした上に、もっと走らせる。新人にしては景気がいいね"),
    line("protagonist", "走らせたいんじゃありません"),
    line("flint", "投げたものが、そのまま消えるかもしれない"),
    line("flint", "初仕事で、ずいぶんな賭けをする"),
    line("protagonist", "賭けじゃありません。怖いのに、あの人は犬を無理に連れ戻さなかった"),
    line("protagonist", "きっと、帰ってきたものを受け取ってくれます"),
    line("flint", "なら《赤いボール》を右のレーンへ。犬と同じ場所を見て、意味が届くところまで見届けて"),
  ],
};

const afterTurn: ScenarioStoryDefinition["afterTurn"] = {
  1: [
    line("protagonist", "数字が増えました"),
    line("flint", "幸福度。目盛りの十五が今夜の理想"),
    line("manager", "ゼロへ落ちれば悪夢。十六以上で朝を迎えれば寝坊でございます"),
    line("protagonist", "よい夢なら、多いほどいいわけじゃないんですね"),
    line("flint", "火も同じ。温かさと火事は、隣り合わせ"),
  ],
  2: [
    narration("火花に気づいた男の不安が増す一方、犬は新しい草の匂いへ尾を振っている。", {
      backdrop: "dreamStreet",
    }),
    line("protagonist", "盤面は一つなのに、二人は違う場所を見ている"),
    line("manager", "同じ夢でも、朝は二つございます"),
  ],
  3: [
    line("protagonist", "さっき置いた夢が、浅いところへ動いています", { backdrop: "dreamStreet" }),
    line("flint", "朝へ近づいた。夢は毎時間、深度を一つ失う。深度で意味が変わるものもある"),
    line("protagonist", "置いた後まで見ないといけないんですね"),
    line("flint", "火花を落とすだけなら簡単。何が燃え残るかを見る方が難しい"),
  ],
  4: [
    line("man", "ここは、どこなんだ", { backdrop: "dreamStreet" }),
    line("protagonist", "男の人が怖がっています"),
    line("flint", "犬は喜んでいる。私のご主人は、この散歩が終わらなければいいと思ってる"),
    line("protagonist", "だからって、私のご主人を怖がらせなくても"),
    line("flint", "同じものを、片方だけに見せることはできない"),
    line("flint", "犬の喜びを大きくすれば男の道は長くなる。男を帰せば犬の散歩は終わる"),
    line("flint", "さて。君は、何を選ぶ？"),
  ],
  5: [
    narration("主人公が夢へ深く手を入れる。古い引き綱、使われなくなった食器、棚の下でほこりをかぶった赤いボール。", {
      backdrop: "dreamStreet",
    }),
    line("protagonist", "見つけた"),
    line("flint", "次の一時間で、それを夢へ置く。献身は答えじゃない。答えに触れるための手つきだよ"),
  ],
  6: [
    narration("赤いボールが、男の靴先へ転がる。", { backdrop: "ballReturnStill" }),
    line("man", "これ……まだあったのか", { backdrop: "dreamStreet" }),
    line("dog", "わん！", { cue: "dog" }),
    line("man", "一回だけだぞ"),
    narration("男がボールを投げる。犬は夕暮れの道を走り、角を曲がって見えなくなる。男の顔へ不安が戻る。"),
    narration("やがて犬は、ボールをくわえてまっすぐ男のもとへ帰ってくる。", {
      backdrop: "ballReturnStill",
    }),
    line("man", "持ってきたのか"),
    line("dog", "わん！", { cue: "dog" }),
    narration("二度目も、三度目も。犬は見えなくなり、必ず帰ってくる。"),
    line("man", "ちゃんと、帰ってくるんだな"),
    narration("その言葉が夢の中へ響く。同じ街灯と郵便受けが一つずつ消え、風に温かな食事の匂いが混ざった。"),
    line("man", "帰って、ごはんにしようか"),
    line("dog", "わん！", { cue: "dog" }),
    narration("男と犬は、一つだけ残った家の明かりへ並んで歩き出す。", {
      backdrop: "walkingHomeStill",
    }),
    line("flint", "事件の火が、そっちへ移ったか", { backdrop: "dreamStreet" }),
    line("protagonist", "火事にはなりませんでした"),
    line("flint", "始まったものが、全部燃えるわけじゃない"),
    line("flint", "悪くない"),
  ],
};

const resonanceResult: ScenarioStoryDefinition["resonanceResult"] = {
  resonance: [
    line("protagonist", "同じ夢が、急に近くなった"),
    line("flint", "向こうも同時に見たから。今のが共鳴。喜びも恐れも、よく響く"),
  ],
  split: [
    line("flint", "視線が分かれた。共鳴を避けるのも、ひとつの読みだよ"),
  ],
};

const resultScenes: ScenarioStoryDefinition["resultScenes"] = {
  ideal: [
    line("flint", "散歩を終わらせずに、帰らせたか"),
    line("protagonist", "帰るところまでが、散歩ですから"),
  ],
  oversleep: [
    line("flint", "少し甘くしすぎた。でも、帰り道は見つけたね"),
    line("protagonist", "次は、幕を引く時刻まで見ます"),
  ],
  low: [
    line("flint", "余韻は少し冷たい。それでも、帰り道はできた"),
    line("protagonist", "次は、もう少し温かなところへ"),
  ],
  nightmare: [
    line("flint", "夜が先に砕けた。どこで恐れが燃え広がったか、忘れないで"),
    line("protagonist", "次は、そこから読み直します"),
  ],
};

const resultCommon: readonly StoryEntry[] = [
  narration("支配人が杖を掲げる。男と犬が並んで帰る姿が、一枚の絵のように《窓》へ残った。", {
    backdrop: "walkingHomeStill",
  }),
  line("manager", "さてさて、まさかまさかのどんでん返し", { backdrop: "nightEyes" }),
  line("manager", "終わらぬ散歩の怪談が、赤いボールをひとつ追いかけ、帰り支度の物語へ"),
  line("manager", "ホラーが一転、ハートフル。ひとさじの甘さとともに、今夜は幕引きでございます"),
  narration("暗い客席から拍手が起こる。水滴や、紙をめくる音や、遠い雷にも聞こえる。", {
    cue: "applause",
  }),
];

const closing: readonly StoryEntry[] = [
  narration("《窓》から手を抜くと、男と犬の夢が指の間から水のように落ち、黒い水面へ戻っていく。", {
    backdrop: "theater",
    cue: "water",
  }),
  narration("最後の一滴だけが水ではなく、淡い砂粒となって舞台へ転がる。けれど、誰も気づかない。"),
  line("protagonist", "終わった……"),
  line("manager", "初舞台、お疲れさまでございました"),
  line("protagonist", "あの二人は、よい朝を迎えられますか？"),
  line("manager", "それを知るころには、もう私どもの出番ではありません"),
  line("manager", "夜明けの先は、ご主人のものでございます"),
  line("protagonist", "でも、きっと大丈夫です"),
  line("manager", "そうお思いになるのも、しもべの自由でございます"),
  narration("《火打石》が舞台袖へ戻っていく。主人公はその背中を追った。", { backdrop: "backstage" }),
  line("protagonist", "《火打石》さん"),
  line("protagonist", "私、ちゃんと仕事ができていましたか"),
  line("flint", "拍手は聞こえたでしょ"),
  line("protagonist", "お客様にとって面白かったことと、仕事ができたことは同じですか？"),
  line("flint", "同じじゃない"),
  line("protagonist", "では、どちらでした？"),
  line("flint", "少なくとも、退屈はしなかった"),
  line("flint", "君も、夢も"),
  line("protagonist", "それ、褒めていますよね"),
  line("flint", "好きに読み替えれば"),
  line("protagonist", "次は、もっと面白くできます"),
  line("flint", "火を大きくすることばかり覚えないでよ"),
  line("protagonist", "では、上手に消す方法も覚えます"),
  narration("《火打石》は、ごく小さく笑った。"),
  line("manager", "今宵の演目は、これにておしまい", { backdrop: "theater" }),
  line("manager", "眠りの続きをお求めの皆様も、どうか、夜明けまではよい夢を"),
  narration("幕が下りる。", { backdrop: "dark" }),
];

const postscriptOpening: readonly StoryEntry[] = [
  title("劇場の誰も知らない", "その朝", { backdrop: "dark" }),
  narration("薄い朝の光が、寝室のカーテンを白くしている。男が目を覚ます。", {
    backdrop: "morningBedroom",
  }),
  narration("何か夢を見ていたような気はする。けれど、どこを歩いていたのかも、誰と話したのかも思い出せない。"),
];

const postscriptScenes: ScenarioStoryDefinition["postscriptScenes"] = {
  ideal: [
    narration("寝床で丸くなっているはずの犬が、ゆっくりと男のそばまで歩いてくる。"),
  ],
  oversleep: [
    narration("少し遅い朝。犬は男が身を起こすと耳を動かし、ゆっくりそばへ歩いてくる。"),
  ],
  low: [
    narration("犬はしばらく寝床から男を見ていた。やがて時間をかけて立ち上がり、そばまで歩いてくる。"),
  ],
  nightmare: [
    narration("犬は小さな音に身をこわばらせる。男の声を確かめると、ゆっくりそばへ歩いてくる。"),
  ],
};

const postscriptCommon: readonly StoryEntry[] = [
  narration("男が手を伸ばすと、犬はその手へ鼻先を寄せる。", { backdrop: "morningStill" }),
  line("man", "おはよう", { backdrop: "morningBedroom" }),
  narration("犬の尾が、床を一度だけ打つ。男はなぜか棚の下へ目を向け、傷だらけの赤いボールを見つけた。"),
  line("man", "ごはんにしようか"),
  line("dog", "わん", { cue: "dog" }),
  narration("台所で二つの食器が触れ合い、朝の光が少しだけ明るくなる。", {
    backdrop: "morningStill",
  }),
  title("第一章", "帰る匂い　了", { backdrop: "dark" }),
];

export const CHAPTER_ONE_STORY: ScenarioStoryDefinition = {
  speakers,
  backdrops,
  opening,
  beforeTurn,
  afterTurn,
  resonanceResult,
  resultScenes,
  resultCommon,
  closing,
  postscriptOpening,
  postscriptScenes,
  postscriptCommon,
};
